/**
 * Browser Maintenance Utility
 * Prevents browser-related performance issues and crashes
 * Should be run periodically to maintain browser health
 */

class BrowserMaintenance {
  constructor() {
    this.maintenanceInterval = null;
    this.lastMaintenance = null;
    this.maintenanceHistory = [];
    this._initialized = false;
    this._running = false;
  }

  // Initialize maintenance system
  init() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;
    // Run maintenance on page load
    this.runMaintenance();

    // Set up periodic maintenance (every 30 minutes)
    this.maintenanceInterval = setInterval(() => {
      this.runMaintenance();
    }, 30 * 60 * 1000);

    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });

    // Monitor for memory issues
    this.monitorMemoryUsage();

    console.log('[BrowserMaintenance] Initialized with 30-minute maintenance intervals');
  }

  // Run comprehensive maintenance
  async runMaintenance() {
    if (this._running) {
      console.log('[BrowserMaintenance] Maintenance already running; skipping overlapping run');
      return;
    }
    this._running = true;
    const startTime = Date.now();
    const maintenanceId = `MAINT_${Date.now()}`;

    try {
      console.log(`[BrowserMaintenance] Starting maintenance session ${maintenanceId}`);

      const results = {
        maintenanceId,
        timestamp: new Date().toISOString(),
        actions: []
      };

      // 1. Validate cache version and clear if outdated
      const cacheVersionCleared = await this.validateCacheVersion();
      if (cacheVersionCleared) {
        results.actions.push('Cleared outdated cache version');
      }

      // 2. Clean up expired cache entries with enhanced validation
      const cacheCleaned = await this.cleanExpiredCache();
      if (cacheCleaned > 0) {
        results.actions.push(`Cleaned ${cacheCleaned} cache entries (expired/corrupted/large)`);
      }

      // 3. Update cache version after successful maintenance
      const newVersion = await this.addCacheVersioning();
      if (newVersion) {
        results.actions.push(`Updated cache version to ${newVersion}`);
      }

      // 4. Comprehensive storage corruption recovery
      const recoveryResults = await this.recoverFromStorageCorruption();
      if (recoveryResults.totalIssues > 0) {
        results.actions.push(`Storage recovery: ${recoveryResults.localStorage.recovered} recovered, ${recoveryResults.localStorage.failed} failed`);
      } else if (!recoveryResults.error) {
        results.actions.push('Storage integrity validated successfully');
      }

      // 5. Clean up event listeners (if possible)
      const listenersCleaned = this.cleanEventListeners();
      if (listenersCleaned > 0) {
        results.actions.push(`Cleaned ${listenersCleaned} event listeners`);
      }

      // 6. Check memory usage and suggest cleanup
      const memoryStatus = this.checkMemoryUsage();
      if (memoryStatus.warning) {
        results.actions.push(`Memory usage warning: ${memoryStatus.message}`);
      }

      // 7. Validate service worker health
      const swStatus = await this.validateServiceWorker();
      if (swStatus.issues.length > 0) {
        results.actions.push(`Service worker issues: ${swStatus.issues.join(', ')}`);
      }

      const duration = Date.now() - startTime;
      results.duration = duration;
      results.success = true;

      this.maintenanceHistory.push(results);
      this.lastMaintenance = Date.now();

      // Keep only last 10 maintenance sessions
      if (this.maintenanceHistory.length > 10) {
        this.maintenanceHistory = this.maintenanceHistory.slice(-10);
      }

      console.log(`[BrowserMaintenance] Maintenance completed in ${duration}ms`, results);

      return results;
    } catch (error) {
      console.error('[BrowserMaintenance] Maintenance failed:', error);

      const errorResult = {
        maintenanceId,
        timestamp: new Date().toISOString(),
        success: false,
        error: error.message,
        duration: Date.now() - startTime
      };

      this.maintenanceHistory.push(errorResult);
      return errorResult;
    } finally {
      this._running = false;
    }
  }

  // Enhanced cache cleaning with better validation
  async cleanExpiredCache() {
    let cleaned = 0;
    let totalCacheSize = 0;
    const maxCacheAge = 24 * 60 * 60 * 1000; // 24 hours
    const maxCacheSize = 50 * 1024 * 1024; // 50MB per cache
    const cacheStats = {};

    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        console.log(`[BrowserMaintenance] Found ${cacheNames.length} cache(s) to clean`);

        for (const cacheName of cacheNames) {
          try {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();
            cacheStats[cacheName] = { total: keys.length, cleaned: 0, size: 0 };

            // Calculate cache size and clean expired entries
            for (const request of keys) {
              try {
                const response = await cache.match(request);
                if (response) {
                  // Check cache age using multiple headers
                  let cacheAge = 0;
                  const date = response.headers.get('date');
                  const lastModified = response.headers.get('last-modified');
                  const expires = response.headers.get('expires');

                  if (date) {
                    cacheAge = Date.now() - new Date(date).getTime();
                  } else if (lastModified) {
                    cacheAge = Date.now() - new Date(lastModified).getTime();
                  }

                  // Check for explicit expiration
                  let isExpired = false;
                  if (expires) {
                    isExpired = new Date(expires).getTime() < Date.now();
                  }

                  // Estimate response size (rough calculation)
                  const contentLength = response.headers.get('content-length');
                  const estimatedSize = contentLength ? parseInt(contentLength) : 1024; // 1KB default
                  cacheStats[cacheName].size += estimatedSize;
                  totalCacheSize += estimatedSize;

                  // Clean based on age, expiration, or size limits
                  const shouldClean = (
                    cacheAge > maxCacheAge || // Too old
                    isExpired || // Explicitly expired
                    totalCacheSize > maxCacheSize || // Cache too large
                    this.isCacheEntryCorrupted(response) // Corrupted data
                  );

                  if (shouldClean) {
                    await cache.delete(request);
                    cleaned++;
                    cacheStats[cacheName].cleaned++;
                    totalCacheSize -= estimatedSize;
                    console.log(`[BrowserMaintenance] Cleaned cache entry: ${request.url} (${Math.round(cacheAge / 1000 / 60)}min old)`);
                  }
                }
              } catch (entryError) {
                console.warn(`[BrowserMaintenance] Error processing cache entry ${request.url}:`, entryError);
                // Try to delete problematic entries
                try {
                  await cache.delete(request);
                  cleaned++;
                  cacheStats[cacheName].cleaned++;
                } catch (deleteError) {
                  console.warn(`[BrowserMaintenance] Failed to delete problematic cache entry:`, deleteError);
                }
              }
            }

            // Clean up empty caches
            if (cacheStats[cacheName].total > 0 && cacheStats[cacheName].total === cacheStats[cacheName].cleaned) {
              try {
                await caches.delete(cacheName);
                console.log(`[BrowserMaintenance] Deleted empty cache: ${cacheName}`);
              } catch (deleteError) {
                console.warn(`[BrowserMaintenance] Failed to delete cache ${cacheName}:`, deleteError);
              }
            }

          } catch (cacheError) {
            console.warn(`[BrowserMaintenance] Error processing cache ${cacheName}:`, cacheError);
          }
        }

        console.log(`[BrowserMaintenance] Cache cleanup completed:`, {
          cachesProcessed: cacheNames.length,
          totalEntriesCleaned: cleaned,
          totalCacheSize: Math.round(totalCacheSize / 1024 / 1024) + 'MB',
          cacheStats
        });

      } else {
        console.log('[BrowserMaintenance] Cache API not available');
      }
    } catch (error) {
      console.error('[BrowserMaintenance] Cache cleanup failed:', error);
    }

    return cleaned;
  }

  // Check if a cache entry appears to be corrupted
  isCacheEntryCorrupted(response) {
    try {
      // Check for common corruption indicators
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');

      // If content-length is 0 or suspiciously small for certain content types
      if (contentLength === '0') {
        return true;
      }

      // Check for mismatched content types and file extensions
      const url = response.url;
      if (url) {
        const extension = url.split('.').pop().toLowerCase();
        if (contentType) {
          // Basic validation - can be expanded
          if (extension === 'js' && !contentType.includes('javascript') && !contentType.includes('text')) {
            return true;
          }
          if (extension === 'css' && !contentType.includes('css') && !contentType.includes('text')) {
            return true;
          }
          if ((extension === 'png' || extension === 'jpg' || extension === 'jpeg') &&
              !contentType.includes('image')) {
            return true;
          }
        }
      }

      return false;
    } catch (error) {
      // If we can't check, assume it's potentially corrupted
      console.warn('[BrowserMaintenance] Error checking cache corruption:', error);
      return true;
    }
  }

  // Add cache versioning for better cache management
  async addCacheVersioning() {
    try {
      if ('caches' in window) {
        const version = Date.now().toString();
        const versionCache = await caches.open('app-version');
        const versionData = { version, timestamp: new Date().toISOString() };

        await versionCache.put('/app-version', new Response(JSON.stringify(versionData)));
        console.log(`[BrowserMaintenance] Cache version set to: ${version}`);

        return version;
      }
    } catch (error) {
      console.warn('[BrowserMaintenance] Failed to set cache version:', error);
    }

    return null;
  }

  // Validate cache version and clear if outdated
  async validateCacheVersion() {
    try {
      if ('caches' in window) {
        const versionCache = await caches.open('app-version');
        const versionResponse = await versionCache.match('/app-version');

        if (versionResponse) {
          const versionData = await versionResponse.json();
          const cacheAge = Date.now() - new Date(versionData.timestamp).getTime();
          const maxVersionAge = 7 * 24 * 60 * 60 * 1000; // 7 days

          if (cacheAge > maxVersionAge) {
            console.log('[BrowserMaintenance] Cache version expired, clearing all caches');
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            return true; // Cache was cleared
          }
        }
      }
    } catch (error) {
      console.warn('[BrowserMaintenance] Failed to validate cache version:', error);
    }

    return false; // Cache is still valid
  }

  // Validate storage integrity with enhanced error handling
  async validateStorageIntegrity() {
    const issues = [];
    const stats = { localStorage: 0, sessionStorage: 0, corrupted: 0, oversized: 0 };

    try {
      // Enhanced localStorage validation
      const localStorageKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          localStorageKeys.push(key);
          stats.localStorage++;
        }
      }

      // Check each localStorage item with better error handling
      for (const key of localStorageKeys) {
        try {
          const value = localStorage.getItem(key);
          if (value === null) continue; // Item was removed during iteration

          // Check for oversized items (limit to 5MB per item)
          if (value.length > 5 * 1024 * 1024) {
            console.warn(`[BrowserMaintenance] Oversized localStorage item: ${key} (${Math.round(value.length / 1024 / 1024)}MB)`);
            issues.push(`Oversized localStorage item: ${key}`);
            stats.oversized++;
            continue;
          }

          // Validate JSON for JSON-looking data
          if (value && (value.startsWith('{') || value.startsWith('['))) {
            try {
              const parsed = JSON.parse(value);
              // Additional validation for known data structures
              if (key.startsWith('finding_actions_') || key.startsWith('action_history_')) {
                if (!Array.isArray(parsed) && typeof parsed !== 'object') {
                  throw new Error('Invalid data structure for scan actions');
                }
              }
            } catch (jsonError) {
              console.warn(`[BrowserMaintenance] Corrupted localStorage JSON: ${key}`, jsonError.message);
              localStorage.removeItem(key);
              issues.push(`Removed corrupted localStorage JSON: ${key}`);
              stats.corrupted++;
            }
          }

          // Check for expired tokens (simple heuristic)
          if (key === 'token' || key === 'refreshToken') {
            try {
              const payload = key === 'token' ? JSON.parse(atob(value.split('.')[1])) : null;
              if (payload && payload.exp && payload.exp * 1000 < Date.now()) {
                console.log(`[BrowserMaintenance] Removing expired ${key}`);
                localStorage.removeItem(key);
                issues.push(`Removed expired ${key}`);
              }
            } catch (e) {
              // Invalid token format, will be handled by corruption check above
            }
          }
        } catch (error) {
          console.warn(`[BrowserMaintenance] Error validating localStorage item ${key}:`, error);
          try {
            localStorage.removeItem(key);
            issues.push(`Removed problematic localStorage item: ${key}`);
            stats.corrupted++;
          } catch (removeError) {
            console.error(`[BrowserMaintenance] Failed to remove localStorage item ${key}:`, removeError);
          }
        }
      }

      // Enhanced sessionStorage validation
      const sessionStorageKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          sessionStorageKeys.push(key);
          stats.sessionStorage++;
        }
      }

      for (const key of sessionStorageKeys) {
        try {
          const value = sessionStorage.getItem(key);
          if (value === null) continue;

          // Check for oversized items (limit to 10MB per item for sessionStorage)
          if (value.length > 10 * 1024 * 1024) {
            console.warn(`[BrowserMaintenance] Oversized sessionStorage item: ${key} (${Math.round(value.length / 1024 / 1024)}MB)`);
            issues.push(`Oversized sessionStorage item: ${key}`);
            stats.oversized++;
            continue;
          }

          if (value && (value.startsWith('{') || value.startsWith('['))) {
            try {
              JSON.parse(value);
            } catch (jsonError) {
              console.warn(`[BrowserMaintenance] Corrupted sessionStorage JSON: ${key}`, jsonError.message);
              sessionStorage.removeItem(key);
              issues.push(`Removed corrupted sessionStorage JSON: ${key}`);
              stats.corrupted++;
            }
          }
        } catch (error) {
          console.warn(`[BrowserMaintenance] Error validating sessionStorage item ${key}:`, error);
          try {
            sessionStorage.removeItem(key);
            issues.push(`Removed problematic sessionStorage item: ${key}`);
            stats.corrupted++;
          } catch (removeError) {
            console.error(`[BrowserMaintenance] Failed to remove sessionStorage item ${key}:`, removeError);
          }
        }
      }

      // Check for storage quota issues
      try {
        const testKey = '__storage_test_' + Date.now();
        const testValue = 'x'.repeat(1024); // 1KB test
        localStorage.setItem(testKey, testValue);
        localStorage.removeItem(testKey);
      } catch (quotaError) {
        console.warn('[BrowserMaintenance] Storage quota exceeded, clearing old items');
        issues.push('Storage quota exceeded - cleared old items');
        this.clearOldStorageItems();
      }

      console.log(`[BrowserMaintenance] Storage validation completed:`, stats);
      if (issues.length > 0) {
        console.log(`[BrowserMaintenance] Storage issues found:`, issues);
      }

    } catch (error) {
      console.error('[BrowserMaintenance] Storage validation failed:', error);
      issues.push(`Storage validation error: ${error.message}`);
    }

    return issues;
  }

  // Clear old/unused storage items to free up space
  clearOldStorageItems() {
    try {
      const keysToRemove = [];
      const now = Date.now();
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days

      // Check localStorage for old items
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('__') && key.includes('_')) {
          // Remove temporary/test items
          keysToRemove.push(key);
        } else if (key && (key.includes('timestamp') || key.includes('lastUpdate'))) {
          try {
            const value = localStorage.getItem(key);
            if (value && (value.includes('timestamp') || value.includes('lastUpdate'))) {
              const itemData = JSON.parse(value);
              if (itemData.timestamp && (now - new Date(itemData.timestamp).getTime()) > maxAge) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }

      // Remove identified old items
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`[BrowserMaintenance] Removed old storage item: ${key}`);
        } catch (e) {
          console.warn(`[BrowserMaintenance] Failed to remove old item: ${key}`);
        }
      });

      console.log(`[BrowserMaintenance] Cleared ${keysToRemove.length} old storage items`);
    } catch (error) {
      console.warn('[BrowserMaintenance] Error clearing old storage items:', error);
    }
  }

  // Comprehensive storage corruption recovery
  async recoverFromStorageCorruption() {
    const recoveryResults = {
      localStorage: { recovered: 0, failed: 0, backedUp: 0 },
      sessionStorage: { recovered: 0, failed: 0, backedUp: 0 },
      totalIssues: 0
    };

    try {
      console.log('[BrowserMaintenance] Starting comprehensive storage corruption recovery');

      // Create backup before recovery
      await this.createStorageBackup();

      // Recover localStorage
      recoveryResults.localStorage = await this.recoverLocalStorage();

      // Recover sessionStorage
      recoveryResults.sessionStorage = await this.recoverSessionStorage();

      // Attempt to restore critical data from backup if recovery failed
      if (recoveryResults.localStorage.failed > 0 || recoveryResults.sessionStorage.failed > 0) {
        console.log('[BrowserMaintenance] Recovery partially failed, attempting restoration from backup');
        await this.restoreFromBackup();
      }

      recoveryResults.totalIssues = recoveryResults.localStorage.failed + recoveryResults.sessionStorage.failed;

      console.log('[BrowserMaintenance] Storage recovery completed:', recoveryResults);
      return recoveryResults;

    } catch (error) {
      console.error('[BrowserMaintenance] Storage recovery failed:', error);
      // Emergency fallback - clear everything
      await this.emergencyStorageReset();
      return { error: error.message, emergencyReset: true };
    }
  }

  // Create backup of storage before recovery
  async createStorageBackup() {
    try {
      // Check if we have enough quota before creating backup
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const percentUsed = (estimate.usage / estimate.quota) * 100;
        
        // Skip backup if storage is more than 80% full
        if (percentUsed > 80) {
          console.warn('[BrowserMaintenance] Storage quota exceeded, skipping backup');
          return null;
        }
      }

      const backup = {
        localStorage: {},
        sessionStorage: {},
        timestamp: new Date().toISOString(),
        version: '1.0'
      };

      // Backup only essential localStorage keys (limit size)
      const essentialKeys = ['token', 'refreshToken', 'user'];
      for (const key of essentialKeys) {
        if (localStorage.getItem(key)) {
          try {
            backup.localStorage[key] = localStorage.getItem(key);
          } catch (e) {
            console.warn(`[BrowserMaintenance] Failed to backup localStorage key: ${key}`);
          }
        }
      }

      // Don't backup sessionStorage (not critical)
      // backup.sessionStorage = {}; // Skip to save space

      // Store backup with size limit check
      const backupKey = `__storage_backup_${Date.now()}`;
      const backupString = JSON.stringify(backup);
      
      // Only store if backup is reasonable size (< 1MB)
      if (backupString.length < 1024 * 1024) {
        try {
          localStorage.setItem(backupKey, backupString);
          console.log(`[BrowserMaintenance] Storage backup created: ${backupKey}`);
          
          // Keep only last 2 backups (reduced from 3)
          await this.cleanOldBackups(2);
          
          return backupKey;
        } catch (quotaError) {
          console.warn('[BrowserMaintenance] QuotaExceededError, cleaning old backups...');
          await this.cleanOldBackups(0); // Remove all backups
          return null;
        }
      } else {
        console.warn('[BrowserMaintenance] Backup too large, skipping');
        return null;
      }

    } catch (error) {
      console.error('[BrowserMaintenance] Failed to create storage backup:', error);
      return null;
    }
  }

  // Check if a storage key contains sensitive data
  isSensitiveKey(key) {
    const sensitivePatterns = [
      /^token/i,
      /^password/i,
      /^secret/i,
      /^key/i,
      /^auth/i,
      /^session/i,
      /^refresh/i
    ];

    return sensitivePatterns.some(pattern => pattern.test(key));
  }

  // Recover localStorage with comprehensive error handling
  async recoverLocalStorage() {
    const results = { recovered: 0, failed: 0, backedUp: 0 };
    const keysToProcess = [];

    try {
      // First pass: identify all keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          keysToProcess.push(key);
        }
      }

      // Second pass: validate and recover each item
      for (const key of keysToProcess) {
        try {
          const value = localStorage.getItem(key);

          if (value === null) continue;

          // Validate JSON data
          if (this.isJsonData(value)) {
            const validation = this.validateJsonData(key, value);
            if (!validation.valid) {
              console.warn(`[BrowserMaintenance] Invalid JSON data for key ${key}:`, validation.error);

              // Attempt to repair common JSON issues
              const repaired = this.attemptJsonRepair(key, value);
              if (repaired) {
                localStorage.setItem(key, repaired);
                results.recovered++;
                console.log(`[BrowserMaintenance] Repaired JSON data for key: ${key}`);
              } else {
                // Remove corrupted data
                localStorage.removeItem(key);
                results.failed++;
                console.log(`[BrowserMaintenance] Removed corrupted data for key: ${key}`);
              }
            } else {
              results.recovered++;
            }
          } else {
            results.recovered++;
          }

          // Check for oversized items
          if (value.length > 5 * 1024 * 1024) { // 5MB limit
            console.warn(`[BrowserMaintenance] Oversized item detected: ${key} (${Math.round(value.length / 1024 / 1024)}MB)`);
            // Create compressed version or remove
            if (this.isCompressibleData(key, value)) {
              const compressed = await this.compressStorageData(value);
              if (compressed && compressed.length < value.length * 0.8) {
                localStorage.setItem(key, compressed);
                console.log(`[BrowserMaintenance] Compressed oversized item: ${key}`);
              }
            }
          }

        } catch (error) {
          console.error(`[BrowserMaintenance] Error processing localStorage key ${key}:`, error);
          try {
            localStorage.removeItem(key);
            results.failed++;
          } catch (removeError) {
            console.error(`[BrowserMaintenance] Failed to remove corrupted key ${key}:`, removeError);
          }
        }
      }

    } catch (error) {
      console.error('[BrowserMaintenance] localStorage recovery failed:', error);
    }

    return results;
  }

  // Recover sessionStorage
  async recoverSessionStorage() {
    const results = { recovered: 0, failed: 0, backedUp: 0 };
    const keysToProcess = [];

    try {
      // Similar recovery logic for sessionStorage
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          keysToProcess.push(key);
        }
      }

      for (const key of keysToProcess) {
        try {
          const value = sessionStorage.getItem(key);
          if (value === null) continue;

          if (this.isJsonData(value)) {
            const validation = this.validateJsonData(key, value);
            if (!validation.valid) {
              const repaired = this.attemptJsonRepair(key, value);
              if (repaired) {
                sessionStorage.setItem(key, repaired);
                results.recovered++;
              } else {
                sessionStorage.removeItem(key);
                results.failed++;
              }
            } else {
              results.recovered++;
            }
          } else {
            results.recovered++;
          }
        } catch (error) {
          console.error(`[BrowserMaintenance] Error processing sessionStorage key ${key}:`, error);
          try {
            sessionStorage.removeItem(key);
            results.failed++;
          } catch (removeError) {
            console.error(`[BrowserMaintenance] Failed to remove corrupted sessionStorage key ${key}:`, removeError);
          }
        }
      }

    } catch (error) {
      console.error('[BrowserMaintenance] sessionStorage recovery failed:', error);
    }

    return results;
  }

  // Check if data appears to be JSON
  isJsonData(value) {
    if (!value || typeof value !== 'string') return false;
    return (value.trim().startsWith('{') && value.trim().endsWith('}')) ||
           (value.trim().startsWith('[') && value.trim().endsWith(']'));
  }

  // Validate JSON data structure
  validateJsonData(key, value) {
    try {
      const parsed = JSON.parse(value);

      // Type-specific validation
      if (key.includes('actions') || key.includes('history')) {
        if (!Array.isArray(parsed) && typeof parsed !== 'object') {
          return { valid: false, error: 'Expected array or object for actions/history data' };
        }
      }

      if (key.includes('timestamp') || key.includes('date')) {
        if (typeof parsed === 'string') {
          const date = new Date(parsed);
          if (isNaN(date.getTime())) {
            return { valid: false, error: 'Invalid date format' };
          }
        }
      }

      return { valid: true };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }

  // Attempt to repair common JSON corruption issues
  attemptJsonRepair(key, value) {
    try {
      // Common repair patterns
      let repaired = value;

      // Fix trailing commas
      repaired = repaired.replace(/,(\s*[}\]])/g, '$1');

      // Fix unquoted keys (simple cases)
      repaired = repaired.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');

      // Fix single quotes to double quotes
      repaired = repaired.replace(/'/g, '"');

      // Try parsing the repaired version
      JSON.parse(repaired);
      console.log(`[BrowserMaintenance] Successfully repaired JSON for key: ${key}`);
      return repaired;

    } catch (error) {
      console.warn(`[BrowserMaintenance] JSON repair failed for key ${key}:`, error);
      return null;
    }
  }

  // Check if data can be compressed
  isCompressibleData(key, value) {
    // Only compress large text data that might benefit from compression
    return value.length > 1024 * 1024 && // > 1MB
           (key.includes('findings') || key.includes('results') || key.includes('data'));
  }

  // Simple data compression (basic implementation)
  async compressStorageData(data) {
    try {
      // For now, just return the original data
      // In a real implementation, you might use compression libraries
      return data;
    } catch (error) {
      console.warn('[BrowserMaintenance] Data compression failed:', error);
      return null;
    }
  }

  // Clean old backup files
  async cleanOldBackups() {
    try {
      const backupKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('__storage_backup_')) {
          backupKeys.push(key);
        }
      }

      // Keep only the 3 most recent backups
      if (backupKeys.length > 3) {
        backupKeys.sort().reverse(); // Sort by timestamp (newest first)
        const keysToRemove = backupKeys.slice(3); // Remove older ones

        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log(`[BrowserMaintenance] Removed old backup: ${key}`);
        });
      }
    } catch (error) {
      console.warn('[BrowserMaintenance] Error cleaning old backups:', error);
    }
  }

  // Restore from backup if recovery failed
  async restoreFromBackup() {
    try {
      // Find the most recent backup
      let latestBackup = null;
      let latestTimestamp = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('__storage_backup_')) {
          const timestamp = parseInt(key.replace('__storage_backup_', ''));
          if (timestamp > latestTimestamp) {
            latestTimestamp = timestamp;
            latestBackup = key;
          }
        }
      }

      if (latestBackup) {
        const backupData = JSON.parse(localStorage.getItem(latestBackup));

        // Restore non-sensitive data
        Object.entries(backupData.localStorage).forEach(([key, value]) => {
          if (!this.isSensitiveKey(key)) {
            try {
              localStorage.setItem(key, value);
              console.log(`[BrowserMaintenance] Restored from backup: ${key}`);
            } catch (e) {
              console.warn(`[BrowserMaintenance] Failed to restore ${key} from backup:`, e);
            }
          }
        });

        console.log(`[BrowserMaintenance] Restored data from backup: ${latestBackup}`);
        return true;
      }

    } catch (error) {
      console.error('[BrowserMaintenance] Backup restoration failed:', error);
    }

    return false;
  }

  // Emergency storage reset as last resort
  async emergencyStorageReset() {
    try {
      console.warn('[BrowserMaintenance] Performing emergency storage reset');

      // Clear all non-critical storage
      const keysToKeep = ['token', 'refreshToken', 'user']; // Keep authentication data

      const allKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) allKeys.push(key);
      }

      allKeys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.warn(`[BrowserMaintenance] Failed to remove ${key} during emergency reset`);
          }
        }
      });

      // Clear sessionStorage completely
      const sessionKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) sessionKeys.push(key);
      }

      sessionKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          console.warn(`[BrowserMaintenance] Failed to remove sessionStorage ${key}`);
        }
      });

      console.log('[BrowserMaintenance] Emergency storage reset completed');
    } catch (error) {
      console.error('[BrowserMaintenance] Emergency storage reset failed:', error);
    }
  }

  // Clean up event listeners (limited capability)
  cleanEventListeners() {
    // This is a best-effort cleanup
    // We can't actually enumerate all event listeners, but we can clean up known ones

    let cleaned = 0;

    try {
      // Clean up any lingering timers
      if (window.maintenanceTimers) {
        window.maintenanceTimers.forEach(timerId => {
          clearTimeout(timerId);
          clearInterval(timerId);
        });
        cleaned += window.maintenanceTimers.length;
        window.maintenanceTimers = [];
      }
    } catch (error) {
      console.warn('[BrowserMaintenance] Event listener cleanup failed:', error);
    }

    return cleaned;
  }

  // Enhanced memory usage monitoring with proactive cleanup
  checkMemoryUsage() {
    try {
      if ('memory' in performance) {
        const memInfo = performance.memory;
        const usedPercent = (memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100;
        const usedMB = Math.round(memInfo.usedJSHeapSize / 1024 / 1024);
        const totalMB = Math.round(memInfo.totalJSHeapSize / 1024 / 1024);
        const limitMB = Math.round(memInfo.jsHeapSizeLimit / 1024 / 1024);

        // Enhanced memory thresholds
        const memoryStatus = {
          usedPercent,
          usedMB,
          totalMB,
          limitMB,
          warning: false,
          critical: false,
          message: '',
          recommendations: []
        };

        if (usedPercent > 90) {
          memoryStatus.critical = true;
          memoryStatus.warning = true;
          memoryStatus.message = `CRITICAL: Memory usage at ${Math.round(usedPercent)}% (${usedMB}MB/${limitMB}MB)`;
          memoryStatus.recommendations = [
            'Immediate browser restart recommended',
            'Clear browser cache and reload',
            'Close unnecessary tabs',
            'Disable browser extensions'
          ];

          // Trigger emergency cleanup
          this.performEmergencyMemoryCleanup();

        } else if (usedPercent > 75) {
          memoryStatus.warning = true;
          memoryStatus.message = `High memory usage: ${Math.round(usedPercent)}% (${usedMB}MB/${limitMB}MB)`;
          memoryStatus.recommendations = [
            'Consider clearing cache',
            'Close unused tabs',
            'Restart browser soon'
          ];

          // Trigger proactive cleanup
          this.performProactiveMemoryCleanup();

        } else if (usedPercent > 60) {
          memoryStatus.message = `Moderate memory usage: ${Math.round(usedPercent)}% (${usedMB}MB/${limitMB}MB)`;
          memoryStatus.recommendations = [
            'Monitor memory usage',
            'Clear cache periodically'
          ];
        }

        // Add memory trend analysis
        if (this.lastMemoryCheck) {
          const memoryIncrease = usedMB - this.lastMemoryCheck.usedMB;
          if (memoryIncrease > 50) { // More than 50MB increase
            memoryStatus.recommendations.unshift('Memory usage increasing rapidly');
          }
        }

        this.lastMemoryCheck = { usedMB, usedPercent, timestamp: Date.now() };

        return memoryStatus;
      }
    } catch (error) {
      console.warn('[BrowserMaintenance] Memory check failed:', error);
    }

    return {
      warning: false,
      critical: false,
      message: 'Memory monitoring not available',
      recommendations: []
    };
  }

  // Perform emergency memory cleanup for critical situations
  async performEmergencyMemoryCleanup() {
    console.log('[BrowserMaintenance] Performing emergency memory cleanup');

    try {
      // Force garbage collection if available
      if (window.gc) {
        window.gc();
        console.log('[BrowserMaintenance] Emergency garbage collection triggered');
      }

      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[BrowserMaintenance] Emergency cache clearing completed');
      }

      // Clear non-essential storage items
      this.clearNonEssentialStorage();

      // Disconnect non-critical connections
      this.disconnectNonCriticalConnections();

      console.log('[BrowserMaintenance] Emergency cleanup completed');
    } catch (error) {
      console.error('[BrowserMaintenance] Emergency cleanup failed:', error);
    }
  }

  // Perform proactive memory cleanup
  async performProactiveMemoryCleanup() {
    console.log('[BrowserMaintenance] Performing proactive memory cleanup');

    try {
      // Clear expired cache entries
      await this.cleanExpiredCache();

      // Clear old storage items
      this.clearOldStorageItems();

      // Suggest browser optimizations
      this.suggestMemoryOptimizations();

      console.log('[BrowserMaintenance] Proactive cleanup completed');
    } catch (error) {
      console.error('[BrowserMaintenance] Proactive cleanup failed:', error);
    }
  }

  // Clear non-essential storage items
  clearNonEssentialStorage() {
    try {
      const keysToRemove = [];

      // Remove temporary items
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('temp_') ||
          key.startsWith('cache_') ||
          key.includes('debug') ||
          key.includes('log')
        )) {
          keysToRemove.push(key);
        }
      }

      // Remove items older than 1 hour
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.includes('timestamp')) {
          try {
            const value = sessionStorage.getItem(key);
            const data = JSON.parse(value);
            if (data.timestamp && new Date(data.timestamp).getTime() < oneHourAgo) {
              keysToRemove.push(key);
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        } catch (e) {
          console.warn(`[BrowserMaintenance] Failed to remove non-essential item: ${key}`);
        }
      });

      console.log(`[BrowserMaintenance] Cleared ${keysToRemove.length} non-essential storage items`);
    } catch (error) {
      console.warn('[BrowserMaintenance] Error clearing non-essential storage:', error);
    }
  }

  // Disconnect non-critical connections
  disconnectNonCriticalConnections() {
    try {
      // Close any lingering SSE connections
      if (window.activeSSEConnections) {
        window.activeSSEConnections.forEach(connection => {
          try {
            if (connection.close) {
              connection.close();
            }
          } catch (e) {
            console.warn('[BrowserMaintenance] Error closing SSE connection:', e);
          }
        });
        window.activeSSEConnections.clear();
      }

      // Close any WebSocket connections (if any)
      if (window.activeWebSockets) {
        window.activeWebSockets.forEach(ws => {
          try {
            if (ws.close) {
              ws.close();
            }
          } catch (e) {
            console.warn('[BrowserMaintenance] Error closing WebSocket:', e);
          }
        });
        window.activeWebSockets.clear();
      }

      console.log('[BrowserMaintenance] Disconnected non-critical connections');
    } catch (error) {
      console.warn('[BrowserMaintenance] Error disconnecting connections:', error);
    }
  }

  // Suggest memory optimizations to user
  suggestMemoryOptimizations() {
    const suggestions = [
      'Close unused browser tabs',
      'Clear browser cache',
      'Disable unnecessary browser extensions',
      'Restart browser periodically',
      'Use incognito mode for heavy applications'
    ];

    console.log('[BrowserMaintenance] Memory optimization suggestions:');
    suggestions.forEach((suggestion, index) => {
      console.log(`${index + 1}. ${suggestion}`);
    });

    // Show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Memory Optimization Suggestions', {
        body: 'Your browser is using high memory. Consider the suggestions in console.',
        icon: '/favicon.ico'
      });
    }
  }

  // Enhanced service worker management and validation
  async validateServiceWorker() {
    const status = {
      healthy: true,
      issues: [],
      registrations: [],
      recommendations: []
    };

    try {
      if ('serviceWorker' in navigator) {
        // Get all service worker registrations
        const registrations = await navigator.serviceWorker.getRegistrations();
        status.registrations = registrations.map(reg => ({
          scope: reg.scope,
          state: reg.active?.state || 'unknown',
          scriptURL: reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL
        }));

        console.log(`[BrowserMaintenance] Found ${registrations.length} service worker registration(s)`);

        for (const registration of registrations) {
          await this.validateSingleServiceWorker(registration, status);
        }

        // Clean up redundant service workers
        await this.cleanupRedundantServiceWorkers(registrations, status);

        // Check for service worker updates
        await this.checkServiceWorkerUpdates(status);

        // Generate recommendations based on findings
        this.generateServiceWorkerRecommendations(status);

      } else {
        status.issues.push('Service Worker API not supported');
        status.recommendations.push('Consider using a modern browser with Service Worker support');
      }
    } catch (error) {
      status.issues.push(`Service worker validation failed: ${error.message}`);
      status.healthy = false;
      console.error('[BrowserMaintenance] Service worker validation error:', error);
    }

    return status;
  }

  // Validate a single service worker registration
  async validateSingleServiceWorker(registration, status) {
    try {
      const scope = registration.scope;
      console.log(`[BrowserMaintenance] Validating SW for scope: ${scope}`);

      // Check service worker states
      const states = {
        installing: registration.installing?.state,
        waiting: registration.waiting?.state,
        active: registration.active?.state
      };

      // Check if service worker is stuck
      if (states.installing === 'installing' || states.waiting === 'waiting') {
        const installTime = Date.now() - (registration.installing?.scriptURL ? 10000 : 0); // Rough estimate
        if (installTime > 30000) { // Stuck for more than 30 seconds
          status.issues.push(`Service worker stuck in ${states.installing || states.waiting} state for scope: ${scope}`);
          status.recommendations.push(`Force reload service worker for scope: ${scope}`);
        }
      }

      // Check if service worker is responding
      if (registration.active) {
        try {
          const pingResponse = await this.pingServiceWorker(registration);
          if (!pingResponse.success) {
            status.issues.push(`Service worker not responding for scope: ${scope}`);
            status.healthy = false;
          }
        } catch (pingError) {
          status.issues.push(`Service worker ping failed for scope: ${scope} - ${pingError.message}`);
          status.healthy = false;
        }
      }

      // Check service worker age
      if (registration.active) {
        const ageCheck = await this.checkServiceWorkerAge(registration);
        if (ageCheck.outdated) {
          status.issues.push(`Service worker outdated for scope: ${scope} (${ageCheck.age} days old)`);
          status.recommendations.push(`Update service worker for scope: ${scope}`);
        }
      }

      // Check for conflicting scopes
      const conflictingScopes = status.registrations.filter(reg =>
        reg.scope !== scope && (
          scope.startsWith(reg.scope) || reg.scope.startsWith(scope)
        )
      );

      if (conflictingScopes.length > 0) {
        status.issues.push(`Conflicting service worker scopes detected: ${scope}`);
        status.recommendations.push('Resolve conflicting service worker scopes');
      }

    } catch (error) {
      status.issues.push(`Error validating service worker for ${registration.scope}: ${error.message}`);
      status.healthy = false;
    }
  }

  // Ping service worker to check if it's responding
  async pingServiceWorker(registration) {
    try {
      // Try to communicate with the service worker
      const messageChannel = new MessageChannel();

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve({ success: false, error: 'Timeout' });
        }, 5000);

        messageChannel.port1.onmessage = (event) => {
          clearTimeout(timeout);
          if (event.data && event.data.type === 'pong') {
            resolve({ success: true });
          } else {
            resolve({ success: false, error: 'Invalid response' });
          }
        };

        // Send ping message
        if (registration.active) {
          registration.active.postMessage({
            type: 'ping',
            timestamp: Date.now()
          }, [messageChannel.port2]);
        } else {
          resolve({ success: false, error: 'No active service worker' });
        }
      });

    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Check service worker age and determine if update is needed
  async checkServiceWorkerAge(registration) {
    try {
      const scriptURL = registration.active?.scriptURL;
      if (!scriptURL) return { outdated: false, age: 0 };

      // Try to get script modification time
      const response = await fetch(scriptURL, { method: 'HEAD' });
      const lastModified = response.headers.get('last-modified');

      if (lastModified) {
        const scriptDate = new Date(lastModified);
        const ageInDays = (Date.now() - scriptDate.getTime()) / (1000 * 60 * 60 * 24);

        return {
          outdated: ageInDays > 7, // Consider outdated if older than 7 days
          age: Math.round(ageInDays)
        };
      }

      return { outdated: false, age: 0 };
    } catch (error) {
      console.warn('[BrowserMaintenance] Could not check service worker age:', error);
      return { outdated: false, age: 0 };
    }
  }

  // Clean up redundant service workers
  async cleanupRedundantServiceWorkers(registrations, status) {
    try {
      const redundantWorkers = [];

      // Find workers with overlapping scopes
      for (let i = 0; i < registrations.length; i++) {
        for (let j = i + 1; j < registrations.length; j++) {
          const reg1 = registrations[i];
          const reg2 = registrations[j];

          if (reg1.scope === reg2.scope) {
            // Same scope - keep the newer one
            const reg1Active = reg1.active;
            const reg2Active = reg2.active;

            if (reg1Active && reg2Active) {
              // Both have active workers - this shouldn't happen normally
              redundantWorkers.push(reg2);
              status.issues.push(`Duplicate service worker for scope: ${reg1.scope}`);
            } else if (!reg1Active && reg2Active) {
              redundantWorkers.push(reg1);
            } else if (reg1Active && !reg2Active) {
              redundantWorkers.push(reg2);
            }
          }
        }
      }

      // Remove redundant workers
      for (const worker of redundantWorkers) {
        try {
          await worker.unregister();
          status.recommendations.push(`Unregistered redundant service worker for scope: ${worker.scope}`);
          console.log(`[BrowserMaintenance] Unregistered redundant service worker: ${worker.scope}`);
        } catch (error) {
          status.issues.push(`Failed to unregister redundant service worker: ${worker.scope}`);
          console.error(`[BrowserMaintenance] Failed to unregister service worker:`, error);
        }
      }

    } catch (error) {
      status.issues.push(`Error cleaning up redundant service workers: ${error.message}`);
      console.error('[BrowserMaintenance] Error in cleanupRedundantServiceWorkers:', error);
    }
  }

  // Check for service worker updates
  async checkServiceWorkerUpdates(status) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        // Check if there's a waiting service worker (update available)
        if (registration.waiting) {
          status.issues.push(`Service worker update available for scope: ${registration.scope}`);
          status.recommendations.push(`Apply pending service worker update for scope: ${registration.scope}`);

          // Optionally auto-activate the update
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Listen for future updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log(`[BrowserMaintenance] Service worker update installed for scope: ${registration.scope}`);
                status.recommendations.push(`New service worker version available for scope: ${registration.scope}`);
              }
            });
          }
        });
      }

    } catch (error) {
      status.issues.push(`Error checking service worker updates: ${error.message}`);
      console.error('[BrowserMaintenance] Error in checkServiceWorkerUpdates:', error);
    }
  }

  // Generate recommendations based on service worker analysis
  generateServiceWorkerRecommendations(status) {
    // General recommendations
    if (status.registrations.length === 0) {
      status.recommendations.push('Consider implementing service worker for better offline experience');
    }

    if (status.registrations.length > 2) {
      status.recommendations.push('Multiple service workers detected - review and consolidate if possible');
    }

    // Specific issue-based recommendations
    if (status.issues.some(issue => issue.includes('stuck'))) {
      status.recommendations.push('Service worker appears stuck - try force refreshing the page');
    }

    if (status.issues.some(issue => issue.includes('not responding'))) {
      status.recommendations.push('Service worker not responding - may need manual intervention');
    }

    if (status.issues.some(issue => issue.includes('outdated'))) {
      status.recommendations.push('Update service worker to latest version');
    }
  }

  // Force update all service workers
  async forceUpdateServiceWorkers() {
    try {
      console.log('[BrowserMaintenance] Forcing service worker updates');

      const registrations = await navigator.serviceWorker.getRegistrations();
      const updatePromises = registrations.map(async (registration) => {
        try {
          // Force update by re-registering
          await registration.update();
          console.log(`[BrowserMaintenance] Forced update for service worker: ${registration.scope}`);
          return { scope: registration.scope, success: true };
        } catch (error) {
          console.error(`[BrowserMaintenance] Failed to update service worker ${registration.scope}:`, error);
          return { scope: registration.scope, success: false, error: error.message };
        }
      });

      const results = await Promise.all(updatePromises);
      console.log('[BrowserMaintenance] Service worker update results:', results);
      return results;

    } catch (error) {
      console.error('[BrowserMaintenance] Error forcing service worker updates:', error);
      return [{ error: error.message }];
    }
  }

  // Unregister all service workers (nuclear option)
  async unregisterAllServiceWorkers() {
    try {
      console.log('[BrowserMaintenance] Unregistering all service workers');

      const registrations = await navigator.serviceWorker.getRegistrations();
      const unregisterPromises = registrations.map(async (registration) => {
        try {
          const success = await registration.unregister();
          console.log(`[BrowserMaintenance] Unregistered service worker: ${registration.scope}`);
          return { scope: registration.scope, success };
        } catch (error) {
          console.error(`[BrowserMaintenance] Failed to unregister service worker ${registration.scope}:`, error);
          return { scope: registration.scope, success: false, error: error.message };
        }
      });

      const results = await Promise.all(unregisterPromises);
      console.log('[BrowserMaintenance] Service worker unregistration results:', results);
      return results;

    } catch (error) {
      console.error('[BrowserMaintenance] Error unregistering service workers:', error);
      return [{ error: error.message }];
    }
  }

  // Monitor memory usage continuously
  monitorMemoryUsage() {
    if ('memory' in performance) {
      setInterval(() => {
        const memInfo = performance.memory;
        const usedPercent = (memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit) * 100;

        if (usedPercent > 85) {
          console.warn(`[BrowserMaintenance] CRITICAL: Memory usage at ${Math.round(usedPercent)}%`);

          // Trigger garbage collection if possible
          if (window.gc) {
            window.gc();
            console.log('[BrowserMaintenance] Manual garbage collection triggered');
          }

          // Suggest memory cleanup
          this.suggestMemoryCleanup();
        }
      }, 60 * 1000); // Check every minute
    }
  }

  // Suggest memory cleanup actions
  suggestMemoryCleanup() {
    console.log('[BrowserMaintenance] Memory cleanup suggestions:');
    console.log('1. Close unused browser tabs');
    console.log('2. Restart browser');
    console.log('3. Clear browser cache');
    console.log('4. Disable browser extensions temporarily');

    // Show user notification if possible
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('High Memory Usage', {
        body: 'Browser memory usage is high. Consider restarting or clearing cache.',
        icon: '/favicon.ico'
      });
    }
  }

  // Get maintenance statistics
  getStats() {
    return {
      lastMaintenance: this.lastMaintenance,
      totalSessions: this.maintenanceHistory.length,
      successfulSessions: this.maintenanceHistory.filter(h => h.success).length,
      failedSessions: this.maintenanceHistory.filter(h => !h.success).length,
      averageDuration: this.maintenanceHistory.reduce((sum, h) => sum + (h.duration || 0), 0) / this.maintenanceHistory.length,
      recentActions: this.maintenanceHistory.slice(-3).flatMap(h => h.actions || [])
    };
  }

  // Cleanup on component unmount
  cleanup() {
    if (this.maintenanceInterval) {
      clearInterval(this.maintenanceInterval);
      this.maintenanceInterval = null;
    }

    // Clean up any maintenance timers
    if (window.maintenanceTimers) {
      window.maintenanceTimers.forEach(timerId => {
        clearTimeout(timerId);
        clearInterval(timerId);
      });
    }

    console.log('[BrowserMaintenance] Cleanup completed');
  }
}

// Create singleton instance
const browserMaintenance = new BrowserMaintenance();

export default browserMaintenance;

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    browserMaintenance.init();
  }, 1000);
}
